import type { FC } from "react";
import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import MuiDrawer, { drawerClasses } from "@mui/material/Drawer";
import { Box, IconButton, InputAdornment, Stack, TextField, Tooltip, Typography } from "@mui/material";
import { useTranslation, type TranslationKey } from "@privacy/translations";
import type { AssistantConversationSummary } from "../../api/assistant";
import { assistantConversationsQueryOptions } from "../../api/assistant";
import { CircularProgress } from "@privacy/ui";
import { customScrollbar } from "../../shared/customScrollbar";
import { ChevronRightIcon, SearchIcon } from "../../shared/icons";
import { useLocalStorage } from "../../shared/useLocalStorage";
import { AssistantBudgetPanel } from "./AssistantBudgetPanel";
import { AssistantConversationRow } from "./AssistantConversationRow";
import { dayGroup } from "./assistantConversationMeta";

const SIDEBAR_WIDTH = "17rem";
const COLLAPSED_WIDTH = "3.5rem";

type SidebarState = "expanded" | "collapsed";

type AssistantHistorySidebarProps = { currentConversationId?: string };

export const AssistantHistorySidebar: FC<AssistantHistorySidebarProps> = ({ currentConversationId }) => {
  const { t } = useTranslation();
  const [state, setState] = useLocalStorage<SidebarState>(
    "privacy:assistant-sidebar-state",
    item => item === "expanded" || item === "collapsed",
    "expanded"
  );
  const [search, setSearch] = useState("");
  const conversationsQuery = useQuery(assistantConversationsQueryOptions());

  const isExpanded = state === "expanded";
  const width = isExpanded ? SIDEBAR_WIDTH : COLLAPSED_WIDTH;
  const query = search.trim().toLowerCase();
  const all = conversationsQuery.data ?? [];
  const conversations = all.filter(conversation => query.length === 0 || matches(conversation, query));

  return (
    <MuiDrawer
      variant="permanent"
      sx={{
        zIndex: 0,
        width,
        flexShrink: 0,
        [`& .${drawerClasses.paper}`]: {
          position: "relative",
          bgcolor: theme => theme.palette.grey["100"],
          borderRight: theme => `1px solid ${theme.palette.divider}`,
          width,
          overflow: "hidden",
        },
      }}
    >
      {isExpanded ? (
        <Stack sx={{ height: "100%", minHeight: 0 }}>
          <Stack direction="row" sx={{ alignItems: "center", justifyContent: "space-between", px: 2, pt: 2, pb: 1 }}>
            <Typography sx={{ fontWeight: 600, fontSize: theme => theme.typography.pxToRem(16) }}>
              {t("Assistant.History.Title")}
            </Typography>
            <Tooltip title={t("Assistant.History.Collapse")}>
              <IconButton
                size="small"
                aria-label={t("Assistant.History.Collapse")}
                onClick={() => setState("collapsed")}
              >
                <ChevronRightIcon size={16} rotate={180} />
              </IconButton>
            </Tooltip>
          </Stack>

          <Stack sx={{ px: 1, gap: 0.5 }}>
            <TextField
              id="assistant-history-search"
              size="small"
              placeholder={t("Assistant.History.Search")}
              value={search}
              onChange={event => setSearch(event.target.value)}
              slotProps={{
                input: {
                  sx: { borderRadius: 1 },
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon size={14} />
                    </InputAdornment>
                  ),
                },
                htmlInput: { "aria-label": t("Assistant.History.Search"), "autoComplete": "off" },
              }}
              sx={{ mx: 1, my: 0.5 }}
            />
          </Stack>

          <Box sx={{ flex: 1, minHeight: 0, overflow: "auto", px: 1, pt: 2, pb: 2, ...customScrollbar }}>
            {conversationsQuery.isPending && (
              <Stack sx={{ alignItems: "center", pt: 3 }}>
                <CircularProgress delay={1000} size={24} />
              </Stack>
            )}
            {conversationsQuery.data && conversations.length === 0 && (
              <Typography sx={{ px: 2, fontSize: theme => theme.typography.pxToRem(14), color: "text.secondary" }}>
                {t(all.length === 0 ? "Assistant.History.Empty" : "Assistant.History.NoMatches")}
              </Typography>
            )}
            <Stack sx={{ gap: 2 }}>
              {groupByDay(conversations).map(group => (
                <Stack key={group.key} sx={{ gap: 0.5 }}>
                  <Typography
                    id={`assistant-history-${group.key}`}
                    sx={{
                      px: 2,
                      fontSize: theme => theme.typography.pxToRem(12),
                      fontWeight: 600,
                      color: "text.secondary",
                    }}
                  >
                    {t(group.labelKey)}{" "}
                    <Box component="span" sx={{ fontWeight: 400 }}>
                      {group.conversations.length}
                    </Box>
                  </Typography>
                  <Box component="ul" aria-labelledby={`assistant-history-${group.key}`} sx={{ m: 0, p: 0 }}>
                    {group.conversations.map(conversation => (
                      <AssistantConversationRow
                        key={conversation.conversationId}
                        conversation={conversation}
                        isCurrent={conversation.conversationId === currentConversationId}
                      />
                    ))}
                  </Box>
                </Stack>
              ))}
            </Stack>
          </Box>

          <AssistantBudgetPanel />
        </Stack>
      ) : (
        <Stack sx={{ alignItems: "center", gap: 1, pt: 2 }}>
          <Tooltip title={t("Assistant.History.Expand")} placement="right">
            <IconButton size="small" aria-label={t("Assistant.History.Expand")} onClick={() => setState("expanded")}>
              <ChevronRightIcon size={16} />
            </IconButton>
          </Tooltip>
        </Stack>
      )}
    </MuiDrawer>
  );
};

const matches = (conversation: AssistantConversationSummary, query: string) =>
  [conversation.title, ...(conversation.records ?? []).map(record => record.name)].some(text =>
    (text ?? "").toLowerCase().includes(query)
  );

const groupByDay = (conversations: Array<AssistantConversationSummary>) => {
  const groups: Array<{
    key: "today" | "yesterday" | "earlier";
    labelKey: TranslationKey;
    conversations: Array<AssistantConversationSummary>;
  }> = [
    { key: "today", labelKey: "Assistant.History.Today", conversations: [] },
    { key: "yesterday", labelKey: "Assistant.History.Yesterday", conversations: [] },
    { key: "earlier", labelKey: "Assistant.History.Earlier", conversations: [] },
  ];
  for (const conversation of conversations) {
    groups.find(group => group.key === dayGroup(conversation.updatedAt))!.conversations.push(conversation);
  }
  return groups.filter(group => group.conversations.length > 0);
};
